import * as React from "react";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createGRN } from "logic/permissions/GRN";
import { IfPermitted } from "components/common";
import UserNotification from "util/UserNotification";
import { MenuItem } from "components/bootstrap";
import BulkActionsDropdown from "components/common/EntityDataTable/BulkActionsDropdown";
import useSelectedEntities from "components/common/EntityDataTable/hooks/useSelectedEntities";
import { COLLECTION_ENTITIES_QUERY_KEY } from "collections/hooks/useCollectionEntities";
import { COLLECTION_CANDIDATES_QUERY_KEY } from "collections/hooks/useCollectionCandidates";
import useCollectionEntityMutation from "collections/hooks/useCollectionEntityMutation";
import { useTranslation } from "react-i18next";
const BulkActions = ({ collectionId, selectedEntities }) => {
  const { t } = useTranslation("enterprise_collections");
  const { bulkAddEntitiesMutation } = useCollectionEntityMutation(collectionId);
  const queryClient = useQueryClient();
  const { setSelectedEntities } = useSelectedEntities();
  const selectedItemsAmount = selectedEntities == null ? void 0 : selectedEntities.length;
  const refetchCollection = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [COLLECTION_ENTITIES_QUERY_KEY, collectionId]
    });
    queryClient.invalidateQueries({
      queryKey: [COLLECTION_CANDIDATES_QUERY_KEY, collectionId]
    });
  }, [queryClient, collectionId]);
  const onAdd = useCallback(() => {
    bulkAddEntitiesMutation.mutateAsync({ addedEntities: selectedEntities.map((entity) => createGRN(entity.type, entity.id)) }).then(({ failures }) => {
      if (failures == null ? void 0 : failures.length) {
        const notUpdatedCollectionEntityIds = failures.map(({ entity_id }) => entity_id);
        setSelectedEntities(notUpdatedCollectionEntityIds);
        UserNotification.error(
          t("entities_bulk.add_failed", {
            count: selectedItemsAmount,
            failed: notUpdatedCollectionEntityIds.length
          })
        );
      } else {
        setSelectedEntities([]);
        UserNotification.success(t("entities_bulk.add_success", { count: selectedItemsAmount }), t("shared.success"));
      }
    }).catch((error) => {
      UserNotification.error(t("entities_bulk.add_error", { error }));
    }).finally(() => {
      refetchCollection();
    });
  }, [bulkAddEntitiesMutation, refetchCollection, selectedEntities, selectedItemsAmount, setSelectedEntities, t]);
  return /* @__PURE__ */ React.createElement(BulkActionsDropdown, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: `collection_entities:edit:${collectionId}` }, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: onAdd }, t("shared.add"))));
};
export default BulkActions;
